import React, { useState, useRef } from 'react';
import { BiChevronDown } from 'react-icons/bi';
import { formatPrice, getThumbnailUrl } from '../utils/helpers';
import { useProductContext } from '../context/product_context';
import { Link } from 'react-router-dom';
import {
  Table,
  Thead,
  Tbody,
  Tr,
  Th,
  Td,
  Image,
  Button,
  Menu,
  MenuButton,
  MenuList,
  MenuItem,
  SimpleGrid,
  VStack,
  HStack,
  Spinner,
  Text,
  useToast,
  AlertDialog,
  AlertDialogBody,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogContent,
  AlertDialogOverlay,
} from '@chakra-ui/react';

function ProductsTable({ products }) {
  const toast = useToast();
  const { fetchProducts, deleteProduct } = useProductContext();
  const [loading, setLoading] = useState(false);
  const [pendingDelete, setPendingDelete] = useState(null); // { id, name } | null
  const cancelRef = useRef();

  const confirmDelete = async () => {
    if (!pendingDelete) return;
    const { id } = pendingDelete;
    setPendingDelete(null);
    setLoading(true);
    const response = await deleteProduct(id);
    setLoading(false);
    if (response.success) {
      toast({
        position: 'top',
        description: response.message,
        status: 'success',
        duration: 5000,
        isClosable: true,
      });
      return await fetchProducts();
    } else {
      return toast({
        position: 'top',
        description: response.message,
        status: 'error',
        duration: 5000,
        isClosable: true,
      });
    }
  };

  return (
    <>
    <SimpleGrid bg='white' p={5} shadow='lg' borderRadius='lg' overflowX='auto'>
      {loading ? (
        <HStack my={8} alignItems='center' justifyContent='center'>
          <Spinner size='lg' color='#FF5252' />
        </HStack>
      ) : (
        <Table variant='simple'>
          <Thead>
            <Tr>
              <Th>Image</Th>
              <Th>Name</Th>
              <Th>Category</Th>
              <Th>Stock</Th>
              <Th></Th>
            </Tr>
          </Thead>
          <Tbody>
            {products.map((product) => {
              const { image, name, price, stock, category, ShopName, id } =
                product;
              return (
                <Tr key={id}>
                  <Td>
                    <Image
                      src={getThumbnailUrl(image, 100)}
                      boxSize='100px'
                      objectFit='cover'
                      borderRadius='lg'
                      referrerPolicy='no-referrer'
                    />
                  </Td>
                  <Td>
                    <VStack alignItems='flex-start' spacing={1}>
                      <Text as='b'>{(name || '').substring(0, 21)}...</Text>
                      <Text fontSize='sm' color='green.500'>
                        {formatPrice(price)}
                      </Text>
                    </VStack>
                  </Td>
                  <Td>
                    <VStack alignItems='flex-start' spacing={1}>
                      <Text as='b'>{category.toUpperCase()}</Text>
                      <Text fontSize='sm' color='brown.500'>
                        {ShopName}
                      </Text>
                    </VStack>
                  </Td>
                  <Td>{stock}</Td>
                  <Td>
                    <Menu>
                      <MenuButton as={Button} rightIcon={<BiChevronDown />}>
                        Actions
                      </MenuButton>
                      <MenuList>
                        <Link to={`/products/${id}`}>
                          <MenuItem>View</MenuItem>
                        </Link>
                        <Link to={`/products/${id}/edit`}>
                          <MenuItem>Edit</MenuItem>
                        </Link>
                        <MenuItem
                          color='red.500'
                          onClick={() => setPendingDelete({ id, name })}
                        >
                          Delete
                        </MenuItem>
                      </MenuList>
                    </Menu>
                  </Td>
                </Tr>
              );
            })}
          </Tbody>
        </Table>
      )}
    </SimpleGrid>

    <AlertDialog
      isOpen={pendingDelete !== null}
      leastDestructiveRef={cancelRef}
      onClose={() => setPendingDelete(null)}
    >
      <AlertDialogOverlay>
        <AlertDialogContent>
          <AlertDialogHeader fontSize='lg' fontWeight='bold'>
            Delete Product
          </AlertDialogHeader>

          <AlertDialogBody>
            Are you sure you want to delete
            {pendingDelete?.name ? ` "${pendingDelete.name}"` : ' this product'}?
            This will also remove its images and cannot be undone.
          </AlertDialogBody>

          <AlertDialogFooter>
            <Button ref={cancelRef} onClick={() => setPendingDelete(null)}>
              Cancel
            </Button>
            <Button colorScheme='red' onClick={confirmDelete} ml={3}>
              Delete
            </Button>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialogOverlay>
    </AlertDialog>
    </>
  );
}

export default ProductsTable;
